import WheelView from './wheel-view';
import WheelsModel from '../model/wheel-model';
import SpinButton from './spin-button'
import * as SUGA from 'suga';

/**
 * Game visualization of the data that model contains.
 */
export default class GameView extends PIXI.Container
{
    /**
     * Constructs the object.
     *
     * @param  {Object}           size             The size
     * @param  {Object}           options          The options
     * @param  {ThemeImageModel}  themeImageModel  The theme image model
     */
    constructor(size, options, themeImageModel)
    {
        super();

        this.placeBet = options.placeBet;
        this.endBet = options.endBet;
        this.resultShown = options.resultShown;
        this.stopAutoplayHandler = options.stopAutoplay;

        this.scales = [1, 0.66, 0.4];
        this.forcedDelayAtStart = 1;
        this.forcedDelayAtEnd = 0;
        this.playing = false;
        this.gameYOffset = 70;

        this.wheelOptions = {
            count: 9,
            startRevolutions: 5,
            startDuration: 2,
            startDelay: 0.2,
            stopRevolutions: 2,
            stopDuration: 2,
            stopDelay: 0.8,
            bounceDegrees: 3,
            bounceDuration: 0.2,
            sasRevolutions: 10,
            sasDuration: 9.3,
            sasDelay: 0,
            sasIndex: 5,
            sasBounceDegrees: 5.0,
            sasBounceDuration: 0.5,
            map: [8, 6, 4, 2, 9, 7, 5, 3, 1],
        };

        this.wheelsModel = new WheelsModel(this.wheelOptions);

        this.addWheel(size, themeImageModel.wheel, this.scales, this.wheelOptions.count);

        this.addWinOutline();

        const mask = this.addFrameMaskAndSas(size, themeImageModel.frame);

        this.wheelView.mask = mask;

        this.addButton(
            size,
            themeImageModel.button,
            themeImageModel.buttonIcon,
            themeImageModel.buttonReflection,
            options.font
        );

        const ticketIdStyle = {
            fontSize: 9,
            fontFamily: options.font,
            fill: 0xffffff,
        };

        if (options.showTicketId)
        {
            this.addTicketId(ticketIdStyle)
        }

        const winStyle = {
            fontFamily: options.font,
            fill: 0xffffff,
        };
        this.addWinSign(size, winStyle);
    }

    /**
     * Used to reset the game for example after replays
     */
    reset()
    {
        this.sas.visible = false;
        this.sign.visible = false;
        this.winOutline.visible = false;
        this.wheelView.reset();
        this.button.reset();
        this.isAuto = false; // look here
    }

    /**
     * lets setup based off a bet
     *
     * @param  {BetModel}  betModel  The bet model
     */
    executeBet(betModel)
    {
        this.playing = true;

        this.stopWinSign();
        this.stopWinOutline();
        this.hideSasInstantly();
        this.isAuto = (betModel.isAutoplay || betModel.isReplay);

        // stop win sound
        this.stopWinSound();

        // show the ticketId
        this.updateTicketId(betModel.ticket.id)

        // build the animation data
        const model = this.wheelsModel.build(betModel);

        // animations
        this.button.spin(1800, 4);
        this.button.showStopText(this.forcedDelayAtStart,this.isAuto)

        // increase volume
        SUGA.Sound.fade('music', 1, 1000);

        this.showSas(model.hasSas, model.wheels[1].totalDuration);

        PIXI.setTimeout(1,() =>
        {
            this.enableInteraction()
        })

        // spin the wheel
        this.wheelView.spin(model, () =>
        {
            const amount = betModel.ticket.totalWinAmount;

            this.completed(betModel);
        });

    }

    /**
     * Stop spinning / End the round
     *
     */
    stop()
    {
        if (!this.playing)
        {
            return;
        }

        this.playing = false;

        this.button.hideText(this.isAuto)
        this.button.stopSpin()

        this.hideSasInstantly();
    }

    /**
     * Called after games ends
     *
     * @param  {Boolean}  isWin   Indicates if window
     * @param  {Number}   amount  The win amount
     */
    completed(betModel)
    {
        const delay = (betModel.ticket.isWin) ? 0 : 1;

        // fade out text
        this.stop()
        this.endSounds();
        this.hideSas(delay);

        // win
        if (betModel.ticket.isWin)
        {
            const promises = [
                this.playWinSound(betModel.ticket.winIndex),
                this.showWinSign(betModel.ticket.totalWinAmount)
            ];

            Promise.all(promises)
            .then(() => this.endBet(betModel))
            .catch((err) => {throw new Error(err)})
            this.showWinOutline();
        }

        // loss
        else
        {
            this.endBet(betModel);
        }

        this.button.showSpinText(this.forcedDelayAtEnd, this.isAuto)
        this.enableInteraction()

        this.resultShown(betModel);
    }

    /**
     * Ends sounds.
     */
    endSounds()
    {
        SUGA.Sound.fade('music', 0.2, 2000);
    }

    /**
     * Adds a frame.
     *
     * @param  {Object}         size  The size
     * @param  {String}         url   The url
     * @return {PIXI.Graphics}  The mask
     */
    addFrameMaskAndSas(size, url)
    {
        this.frame = new PIXI.Sprite.fromImage(url);
        this.addChild(this.frame);
        this.frame.x = Math.round((size.defaultWidth - this.frame.width) / 2);
        this.frame.y = this.gameYOffset;

        const mask = new PIXI.Graphics();

        mask.beginFill(0xFF3300, 0.5);
        mask.moveTo(10, 60);
        mask.lineTo(this.frame.width / 2, 10);
        mask.lineTo(this.frame.width - 10, 60);
        mask.lineTo(this.frame.width - 60, 240);
        mask.lineTo(this.frame.width / 2, this.frame.height);
        mask.lineTo(60, 240);
        mask.lineTo(10, 60);
        mask.endFill();

        this.frame.addChild(mask);

        const textures = SUGA.Loader.makeTexturesFromName('sas');

        this.sas = new PIXI.extras.AnimatedSprite(textures);
        this.frame.addChild(this.sas);
        this.sas.anchor.set(0.5);
        this.sas.scale.set((1 / __dpi) * 2);
        this.sas.animationSpeed = 0.2;
        this.sas.alpha = 0;
        this.sas.x = this.frame.width / 2;
        this.sas.y = 51;

        return mask;
    }

    /**
     * Adds the eye button.
     *
     * @param  {String}  size           The size
     * @param  {String}  url            The url
     * @param  {String}  iconUrl        The icon url
     * @param  {String}  reflectionUrl  The reflection url
     * @param  {String}  font           The font
     */
    addButton(size, url, iconUrl, reflectionUrl, font)
    {
        const handler = (e) => this.buttonClick()
        this.button = new SpinButton(size, url, iconUrl, reflectionUrl, font, handler)
        this.addChild(this.button)
        this.button.y = 317
        this.button.x = 117
    }

    /**
     * On Button Click
     */
    buttonClick()
    {
        this.button.disable();

        if (!this.wheelView.spinning && !this.isAuto)
        {
            this.placeBet();
        }
        else
        {
            SUGA.Sound.stop('sas-louder');
            this.wheelView.stop();
            this.stop();

            if (this.sasTimer)
            {
                this.sasTimer.clear();
            }

            if (this.isAuto)
            {
                this.stopAutoplay();
            }

        }
    }

    stopAutoplay()
    {
        this.resetAutoplay()
        this.stopAutoplayHandler()
    }

    resetAutoplay()
    {
        this.isAuto = false;
        this.button.showSpinText(0, false);
    }

    /**
     * On space bar hit
     */
    spacebarHit()
    {
        if (!this.button.interactiveChildren) { return; }
        this.buttonClick();
    }

    /**
     * Adds a wheel.
     *
     * @param  {Object}  size        The size
     * @param  {String}  url         The url
     * @param  {Array}   scales      The scales
     * @param  {Number}  prizeCount  The prize count
     */
    addWheel(size, url, scales, prizeCount)
    {
        this.wheelView = new WheelView(url, scales, prizeCount);
        this.addChild(this.wheelView);
        this.wheelView.y = 30 + this.gameYOffset;
        this.wheelView.x = (size.defaultWidth - 550) / 2;
    }

    addTicketId(style)
    {
        this.ticketId = new PIXI.Text('',style)
        this.ticketId.position.set(255,370)
        this.ticketId.alpha = 0.5
        this.ticketId.rotation = -0.6
        this.addChild(this.ticketId)
    }

    updateTicketId(ticketId)
    {
        if (!this.ticketId) { return; }

        if (ticketId === 0)
        {
            ticketId = 'DEMO'
        }
        else
        {
            ticketId = "#"+ticketId
        }

        this.ticketId.text = ticketId
    }

    /**
     * Shows the SAS animation
     *
     * @param  {Object}  sas  The sas model
     */
    showSas(hasSas, delay)
    {
        if (!hasSas)
        {
            return;
        }

        const opts = {
            alpha: 1,
            delay,
        };

        this.sas.alpha = 0;
        this.sas.visible = true;
        this.sas.play();
        this.sasTween = window.TweenMax.to(this.sas, 0.5, opts);

        this.sasTimer = PIXI.setTimeout(delay, () =>
        {
            SUGA.Sound.play('sas-louder', 1);
            SUGA.Sound.fade('music', 0.1, 2000);
        });
    }

    /**
     * Hides the sas instantly.
     */
    hideSasInstantly()
    {
        // if we are still waiting
        if (this.hideSasTween)
        {
            this.hideSasTween.kill();
        }

        if (this.sasTween)
        {
            this.sasTween.kill();
            this.sas.stop();
            this.sas.visible = false;
            this.sas.alpha = 0;
        }
    }

    /**
     * Hides the sas over time with fade out.
     *
     * @param  {Number}    delay  The delay
     * @param  {Function}  cb     Completion handler
     */
    hideSas(delay, cb)
    {
        const afterSasRemoval = () =>
        {
            if (this.sas)
            {
                this.sas.stop();
                this.sas.visible = false;
                this.sas.alpha = 0;
            }
            if (cb)
            {
                cb();
            }
        };

        const options = {
            alpha: 0,
            onComplete: afterSasRemoval,
        };

        this.hideSasTween = window.TweenMax.to(this.sas, delay, options);
    }

    /**
     * Adds a window sign.
     *
     * @param  {Object}  size   The size
     * @param  {Object}  style  The style
     */
    addWinSign(size, style)
    {
        const textures = SUGA.Loader.makeTexturesFromName('win-sign');

        this.sign = new PIXI.extras.AnimatedSprite(textures);
        this.addChild(this.sign);
        this.sign.animationSpeed = 0.2;
        this.sign.loop = false;
        this.sign.visible = false;
        this.sign.centerX(size.defaultWidth);
        this.sign.y = 10;

        this.signLabel = new PIXI.Text('', style);
        this.sign.addChild(this.signLabel);
        this.signLabel.centerX();
        this.signLabel.centerY();
        this.signLabel.visible = false;
    }

    /**
     * Shows the window sign.
     *
     * @param  {Number}  amount  The amount
     * @return {Promise}  { description_of_the_return_value }
     */
    showWinSign(amount, cb)
    {
        return new Promise((resolve) =>
        {
            this.sign.onComplete = () =>
            {
                this.signLabel.alpha = 0;
                this.signLabel.visible = true;
                window.TweenMax.to(this.signLabel, 0.1, { alpha: 1, onComplete: resolve});
                if(cb)
                {
                    cb()
                }

            };
            this.sign.visible = true;
            this.sign.animationSpeed = 0.3;
            this.sign.play();

            this.signLabel.text = __c.localize(amount);
            this.signLabel.centerX();
            this.signLabel.centerY();
            this.signLabel.clarify();
        })
    }

    /**
     * Stops a window sign.
     */
    stopWinSign()
    {
        const opts = {
            alpha: 0,
            onComplete: () =>
            {
                this.signLabel.visible = false;
                this.sign.animationSpeed = -0.5;
                this.sign.play();

                this.sign.onComplete = () =>
                {
                    this.sign.visible = false;
                };
            },
        };

        window.TweenMax.to(this.signLabel, 0.05, opts);
    }

    /**
     * Adds a window outline.
     */
    addWinOutline()
    {
        const textures = SUGA.Loader.makeTexturesFromName('win-frame');

        this.winOutline = new PIXI.extras.AnimatedSprite(textures);
        this.wheelView.addChild(this.winOutline);
        this.winOutline.animationSpeed = 0.2;
        this.winOutline.visible = false;
        this.winOutline.alpha = 0;
        this.winOutline.centerX();
        this.winOutline.y = 13;
    }

    /**
     * Shows the window outline.
     */
    showWinOutline()
    {
        this.winOutline.visible = true;
        this.winOutline.play();
        window.TweenMax.to(this.winOutline, 0.05, { alpha: 1 });
    }

    /**
     * Stops a window outline.
     */
    stopWinOutline()
    {
        window.TweenMax.to(this.winOutline, 0.05, { alpha: 0, onComplete: () =>
        {
            this.winOutline.visible = false;
            this.winOutline.stop();
        } });
    }

    playWinSound(index)
    {
        return new Promise((resolve) =>
        {
            switch (index)
            {
                case 1:
                case 2:
                    this.stopSequence = SUGA.Sound.playSequence(['win'],resolve);
                    break;
                case 3:
                case 4:
                    this.stopSequence = SUGA.Sound.playSequence(['win', 'win'],resolve);
                    break;
                case 5:
                case 6:
                    this.stopSequence = SUGA.Sound.playSequence(['win', 'win', 'win'],resolve);
                    break;
                default:
                    this.stopSequence = SUGA.Sound.playSequence(['win', 'win', 'win', 'win'],resolve);
            }
        })

    }

    stopWinSound()
    {
        if (this.stopSequence)
        {
            this.stopSequence();
        }
    }

    enableInteraction()
    {
        this.button.enable()
    }

    disableInteraction()
    {
        this.button.disable()
    }

}



// WEBPACK FOOTER //
// ./themes/fq/view/game-view.js